import { useState } from 'react'
import { setShellReturnToCookie } from 'Hooks/useCommunityCookie'

const EMBED_KEY = 'shell_embed'
const RETURN_TO_KEY = 'shell_embed_return_to'
const INTENDED_URL_KEY = 'shell_embed_intended_url'
const NONPROFIT_ORG_IDS_KEY = 'shell_embed_nonprofit_org_ids'

// Query param shell-app sends with a launch from a nonprofit context: the legacy NonprofitOrg id(s)
// behind the shell org the user is in (several when merged legacy duplicates map to one shell org).
// Without it legacy opens the user's FIRST legacy membership, so anyone in more than one legacy org
// lands in an arbitrary one. Kept in sync with shell-app `LEGACY_NONPROFIT_ORG_IDS_PARAM`.
export const NONPROFIT_ORG_IDS_PARAM = 'nonprofitOrgIds'
const MAX_NONPROFIT_ORG_IDS = 5
const ORG_ID_PATTERN = /^[1-9]\d{0,9}$/

// The param is address-bar input, so anything that is not a short list of positive integers is
// dropped rather than partially trusted. It is never an access grant: the `nonprofitOrg` resolver
// only returns an org the signed-in user is a member of.
export const parseNonprofitOrgIds = (raw: string | null): number[] => {
  if (!raw) return []
  const parts = raw.split(',')
  if (parts.length > MAX_NONPROFIT_ORG_IDS) return []
  if (!parts.every((part) => ORG_ID_PATTERN.test(part))) return []
  const ids = parts.map(Number).filter((id) => Number.isSafeInteger(id))
  return [...new Set(ids)]
}

// The org(s) the current embedded launch asked for, or [] when legacy should pick as it always has.
export const getShellRequestedNonprofitOrgIds = (): number[] => {
  if (typeof window === 'undefined') return []
  try {
    return parseNonprofitOrgIds(sessionStorage.getItem(NONPROFIT_ORG_IDS_KEY))
  } catch {
    return []
  }
}

// Origins allowed to embed the legacy app and receive the user back via the
// "Back to Launchpad" button. Anything outside this list is treated as
// untrusted and the back button will not render. Keep in sync with shell-app
// deploys.
//
// Patterns end with `(\/.*)?$` so they accept the bare origin OR origin + any
// path / query string. Shell-app sends `window.location.href` as `returnTo` so
// the back button can return the user to the exact context (e.g.
// /org/<id>/nonprofit) they came from — not just the marketing root. The
// trailing-slash pattern is greedy on purpose; we trust the allowlisted
// origins to host their own routing, query strings, and hashes.
const RETURN_TO_ALLOWLIST = [
  /^https:\/\/shell\.resilia\.com(\/.*)?$/,
  /^https:\/\/staging-shell\.resilia\.com(\/.*)?$/,
  /^https:\/\/[a-z0-9-]+\.vercel\.app(\/.*)?$/,
  /^http:\/\/localhost:\d+(\/.*)?$/,
]

// Exported for unit testing — this is the security boundary that prevents
// `?returnTo=https://attacker.example` from turning into an open redirect.
export const isAllowedReturnTo = (url: string | null): url is string =>
  !!url && RETURN_TO_ALLOWLIST.some((pattern) => pattern.test(url))

export interface EmbedState {
  isEmbedded: boolean
  returnTo: string | null
}

let bootstrapped = false

// One-shot: read embed params, persist to sessionStorage so they survive
// client-side navigation within the legacy app AND the Auth0 redirect round
// trip, and strip them from the URL so shared/copied links don't leak embed
// mode to other users. MUST be called from index.tsx before React renders so
// it runs before the Auth0 SDK reads `window.location` and replaces our
// query params with `?code=...&state=...` on its callback.
export const bootstrapEmbedMode = () => {
  if (bootstrapped || typeof window === 'undefined') return
  bootstrapped = true

  const params = new URLSearchParams(window.location.search)
  if (params.get('embed') !== 'shell') return

  sessionStorage.setItem(EMBED_KEY, '1')
  // Overwrite on every launch, including with nothing: a grantmaker launch into a tab that last
  // opened a nonprofit org must not keep asking for that org.
  const nonprofitOrgIds = parseNonprofitOrgIds(
    params.get(NONPROFIT_ORG_IDS_PARAM)
  )
  if (nonprofitOrgIds.length > 0) {
    sessionStorage.setItem(NONPROFIT_ORG_IDS_KEY, nonprofitOrgIds.join(','))
  } else {
    sessionStorage.removeItem(NONPROFIT_ORG_IDS_KEY)
  }
  const returnTo = params.get('returnTo')
  if (isAllowedReturnTo(returnTo)) {
    sessionStorage.setItem(RETURN_TO_KEY, returnTo)
    // Mirror to a `.resilia.com` cookie so the signal survives the Auth0
    // round-trip (which lands at `/?code=...` and would otherwise lose the
    // sessionStorage-only embed flag for the purposes of `useShellAppRedirect`
    // on /home). This is the same cookie #6277 sets from CommunityPage; we
    // lift it here so EVERY shell-launched legacy entry (Goals, Resources,
    // Stories, Funder Finder, Community) writes it — not just /community.
    setShellReturnToCookie(returnTo)
  }

  // Stash the original path+search (minus the embed/returnTo we just
  // consumed) so `Auth0ProviderWithHistory` can restore it after Auth0
  // strips the URL down to `/?code=...&state=...`. Without this, PIR users
  // whose silent-SSO fails would land on `/home`, get bounced back to shell
  // by `useShellAppRedirect`, and never reach the legacy app they clicked.
  params.delete('embed')
  params.delete('returnTo')
  params.delete(NONPROFIT_ORG_IDS_PARAM)
  const search = params.toString()
  const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}${
    window.location.hash
  }`
  sessionStorage.setItem(INTENDED_URL_KEY, newUrl)
  window.history.replaceState({}, '', newUrl)
}

// Read-and-clear: returns the URL the user originally requested before the
// Auth0 SDK redirected them to log in. Used by `Auth0ProviderWithHistory`'s
// `onRedirectCallback` to navigate back to that URL rather than the default
// `/` → `/home` fallback.
export const consumeShellIntendedUrl = (): string | null => {
  if (typeof window === 'undefined') return null
  const url = sessionStorage.getItem(INTENDED_URL_KEY)
  if (url) sessionStorage.removeItem(INTENDED_URL_KEY)
  return url
}

// Test-only: reset the module-level `bootstrapped` flag so each test can
// exercise a fresh window.location.search. Module state would otherwise
// persist across tests in the same Jest worker because the module cache
// is shared. NOT for production use.
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle
export const __resetEmbedModeForTesting = () => {
  bootstrapped = false
}

const readState = (): EmbedState => {
  if (typeof window === 'undefined') {
    return { isEmbedded: false, returnTo: null }
  }
  const isEmbedded = sessionStorage.getItem(EMBED_KEY) === '1'
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY)
  return {
    isEmbedded,
    returnTo: isAllowedReturnTo(returnTo) ? returnTo : null,
  }
}

export const useEmbedMode = (): EmbedState => {
  const [state] = useState(() => {
    // Defensive: in case bootstrapEmbedMode() in index.tsx didn't run yet.
    bootstrapEmbedMode()
    return readState()
  })
  return state
}
